import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DxDataGridModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DxiButtonModule, DxiColumnModule, DxiValidationRuleModule, DxoColumnFixingModule, DxoEditingModule, DxoLookupModule } from 'devextreme-angular/ui/nested';
import { ColumnButtonClickEvent, RowDblClickEvent, RowRemovingEvent, SelectionChangedEvent, ToolbarItem, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { BizDateColumnDirective } from '../../../shared/grid/biz-grid-date.directive';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { BizUserColumnDirective } from '../../../shared/grid/biz-grid-user.directive';
import { StatusTrafficLightComponent } from '../../../shared/status-traffic-light/status-traffic-light.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { DateTimeService } from '../../../shared/datetime/datetime.service';
import { EmbeddedItemsService } from '../../services/embedded-items.service';
import { CurrentUserService } from 'src/app/user/services';
import { MonitorGroup } from '../../models/monitor-group';
import { MonitorItem } from '../../models/monitor-item';
import { MonitorSchedule } from '../../models/monitor-schedule';
import { AssemblyArtifact } from 'src/app/developer/models/artifact';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';

@Component({
  selector: 'app-monitor-items-grid',
  templateUrl: './monitor-items-grid.component.html',
  styleUrls: ['./monitor-items-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxTemplateModule, RouterLink, StatusTrafficLightComponent, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, ChangeLogLinkComponent, ChangeLogLinkEntityDirective]
})
export class MonitorItemsGridComponent {
  private readonly router = inject(Router);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly dateTimeService = inject(DateTimeService);
  private readonly embeddedItemsService = inject(EmbeddedItemsService);
  readonly bizGridFiltervalues = inject(BizGridFiltervalues);

  readonly allowAdding = signal(this.currentUserService.hasPermission('Monitor_Action_Item_Create'));
  readonly allowUpdating = signal(this.currentUserService.hasPermission('Monitor_View_Items')); // It's ok to see details; edit button won't be available.
  readonly allowDeleting = signal(this.currentUserService.hasPermission('Monitor_Action_Item_Delete'));
  readonly allowShowLog = signal(this.currentUserService.hasPermission('Monitor_Action_ShowLog'));

  readonly items = input<MonitorItem[]>([]);
  readonly groups = input<MonitorGroup[]>([]);
  readonly schedules = input<MonitorSchedule[]>([]);
  readonly stakeholders = input<Stakeholder[]>([]);
  readonly artifacts = input<AssemblyArtifact[]>([]);
  readonly selectedRows = model<MonitorItem[]>([]);

  readonly deleteItem = output<MonitorItem>();

  private readonly nullSchedule: MonitorSchedule = { Id: 0, Name: 'Select...', Description: '', ScheduledItem: null };
  private readonly nullStakeholder: Stakeholder = this.createNullStakeholder();
  private readonly blanksLabel = '(Blanks)';

  readonly schedulesAndNull = computed<MonitorSchedule[]>(() => [this.nullSchedule, ...this.schedules()]);
  readonly stakeholdersAndNull = computed<Stakeholder[]>(() => [this.nullStakeholder, ...this.stakeholders()]);

  readonly suppressToStatuses: ReadonlyArray<{ id: string; name: string; }> = [
    { id: '', name: '' },
    { id: 'Ok', name: 'Ok' },
    { id: 'Warning', name: 'Warning' }
  ];

  // Override the add button to reuse existing route-based add flow.
  readonly onToolbarPreparing = (event: ToolbarPreparingEvent<MonitorItem, number>): void => {
    const toolbarItems = (event.toolbarOptions.items ?? []) as ToolbarItem[];
    const addRowButton = toolbarItems.find(item => item?.name === 'addRowButton');
    const buttonOptions = addRowButton?.options as { onClick?: () => void; } | undefined;

    if (buttonOptions) {
      buttonOptions.onClick = () => this.addNewItem();
    }
  };

  readonly onRowDblClick = (event: RowDblClickEvent<MonitorItem, number>): void => {
    if (this.allowUpdating()) {
      const item = event.data;
      void this.goToItem(item.id);
    }
  };

  readonly customizeTextSuppressMessagesCount = (cellInfo: Parameters<NonNullable<DxDataGridTypes.Column['customizeText']>>[0]): string => {
    const displayValue = cellInfo.valueText === '0' ? '' : cellInfo.valueText;
    return displayValue;
  };

  readonly calculateSortValueForGroup = (monitorItem: MonitorItem): string => {
    const monitorGroup = this.groups().find(group => group.id === monitorItem.monitorGroupId);
    const sortValue = monitorGroup?.name ?? '';
    return sortValue;
  };

  readonly calculateSortValueForStakeholder = (monitorItem: MonitorItem): string => {
    const stakeholder = this.stakeholders().find(item => item.StakeholdersId === monitorItem.stakeholderId);
    const sortValue = stakeholder?.Name ?? '';
    return sortValue;
  };

  readonly calculateSortValueForStatus = (monitorItem: MonitorItem): string => {
    const sortValue = this.getEffectiveStatus(monitorItem);
    return sortValue;
  };

  readonly calculateStatusFilterExpression = (filterValue: string): ((rowData: MonitorItem) => boolean) =>
    rowData => {
      const statusValue = this.getEffectiveStatus(rowData);
      const matchesFilter = filterValue === this.blanksLabel
        ? statusValue.length === 0
        : statusValue === filterValue;
      return matchesFilter;
    };

  readonly getStatusHeaderFilterData = (): Array<{ text: string; value: string; }> =>
    this.items()
      .flatMap(item => {
        const statusValue = this.getEffectiveStatus(item);
        return statusValue.length > 0 ? [statusValue] : [this.blanksLabel];
      })
      .filter((statusValue, idx, self) => self.indexOf(statusValue) === idx)
      .sort((a, b) => a.localeCompare(b))
      .map(statusValue => ({
        text: statusValue,
        value: statusValue
      }));

  readonly onRowRemoving = (event: RowRemovingEvent<MonitorItem, number>): void => {
    this.deleteItem.emit(event.data);
  };

  readonly editItem = (event: ColumnButtonClickEvent<MonitorItem, number>): void => {
    const item = event.row.data;
    void this.goToItem(item.id);
  };

  readonly getStatusTimestamp = (rowData: MonitorItem): string => {
    const item = this.items().find(currentItem => currentItem.id === rowData.id && currentItem.currentStatus?.StatusDateTime);
    let statusTimestamp = '';

    if (item?.currentStatus?.StatusDateTime) {
      const statusDate = new Date(item.currentStatus.StatusDateTime);
      statusTimestamp = this.dateTimeService.getDevExpressDateTimeFunction(statusDate);
    }

    return statusTimestamp;
  };

  readonly getArtifactType = (monitorItem: MonitorItem): string => {
    const artifact = this.artifacts().find(item => item.ArtifactId === monitorItem.artifactId);
    const artifactType = artifact?.Name ?? this.embeddedItemsService.getDisplayNameForEmbeddedItem(monitorItem.embeddedTypeArea);
    return artifactType;
  };

  readonly selectionChanged = (event: SelectionChangedEvent<MonitorItem, number>): void => {
    const selectedRows = event.selectedRowsData.filter(row => this.isMonitorItem(row));
    this.selectedRows.set(selectedRows);
  };

  private readonly addNewItem = (): void => {
    void this.goToItem(0);
  };

  private readonly goToItem = async (id: number): Promise<boolean> => {
    const isNavigated = await this.router.navigate(['monitor/items/item', { id }]);
    return isNavigated;
  };

  private readonly isMonitorItem = (value: unknown): value is MonitorItem => {
    const candidate = value as Partial<MonitorItem> | null;
    const isItem = !!candidate && typeof candidate === 'object' && typeof candidate.id === 'number';
    return isItem;
  };

  private readonly getEffectiveStatus = (monitorItem: MonitorItem): string =>
    monitorItem.currentStatus?.EffectiveStatus ?? '';

  private createNullStakeholder(): Stakeholder {
    const stakeholder = new Stakeholder();
    stakeholder.StakeholdersId = 0;
    stakeholder.Name = 'Select...';
    return stakeholder;
  }
}
